import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Link } from "gatsby"
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next"
import ScheduleForm from "./schedule-form"

const AdoptionHeader = ({ family = null, agencyId = null, accountId = null, location = null }) => {
  const { t } = useTranslation()
  const { languages, originalPath, language } = useI18next()
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [currentPath, setCurrentPath] = useState("")
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)
  
  const isClient = typeof window !== 'undefined'
  const agencyData = family?.agency || {}
  const familyName = family?.name ? family.name.replace(' / ', ' and ') : "Adoptive Family"
  const recaptchaSiteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY || process.env.RECAPTCHA_SITE_KEY
  
  // Get primary contact info
  let primaryPhone = process.env.PRIMARY_CONTACT_NUMBER ? ('+1' + process.env.PRIMARY_CONTACT_NUMBER.replace(/\D/g, '')) : '+13176527696'
  let textNumber = primaryPhone
  let callNumber = primaryPhone
  if (agencyData) {
    if (agencyData.bm_contact) {
      if (agencyData.bm_contact.text) {
        textNumber = `+1${agencyData.bm_contact.text.replace(/\D/g, '')}`
      }
      if (agencyData.bm_contact.phone) {
        callNumber = `+1${agencyData.bm_contact.phone.replace(/\D/g, '')}`
      }
    } else if (agencyData.phone?.length && agencyData.phone[0]?.number) {
      primaryPhone = (agencyData.phone[0].country_code ? agencyData.phone[0].country_code : '+1') + agencyData.phone[0].number.replace(/\D/g, '')
      textNumber = primaryPhone
      callNumber = primaryPhone
    }
  }
  let bmContactEmail = (process.env.PRIMARY_EMAIL || 'greg.phelps@cairsolutions.com')
  if (agencyData?.bm_contact?.email) {
    bmContactEmail = agencyData.bm_contact.email
  } else if (agencyData?.email?.length) {
    bmContactEmail = agencyData.email[0].email
  }

  // Compact reCAPTCHA loader
  const loadRecaptchaScript = useCallback(async () => {
    if (!recaptchaSiteKey || !isClient) return false
    
    if (window.grecaptcha?.ready) {
      return new Promise(resolve => window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true)
        resolve(true)
      }))
    }

    const existing = document.querySelector(`script[src*="recaptcha/api.js"]`)
    if (existing) {
      return new Promise(resolve => {
        const check = () => window.grecaptcha?.ready ? 
          window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : 
          setTimeout(() => resolve(!!window.grecaptcha), 500)
        
        existing.readyState === 'complete' ? check() : 
        existing.addEventListener('load', check, { once: true })
      })
    }

    return new Promise(resolve => {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`
      script.async = script.defer = true
      script.onload = () => window.grecaptcha?.ready ? 
        window.grecaptcha.ready(() => { setRecaptchaLoaded(true); resolve(true) }) : resolve(false)
      script.onerror = () => resolve(false)
      document.head.appendChild(script)
    })
  }, [recaptchaSiteKey])

  // Compact reCAPTCHA executor
  const executeRecaptcha = useCallback(async (action = 'financial_form') => {
    try {
      if (!isClient) return null
      if (!recaptchaLoaded && !(await loadRecaptchaScript())) return null
      return window.grecaptcha?.execute(recaptchaSiteKey, { action }) || null
    } catch { return null }
  }, [recaptchaLoaded, recaptchaSiteKey, loadRecaptchaScript])
  
  // Financial form data state
  const [financialData, setFinancialData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    dob: '',
    due_date: '',
    baby_gender: '',
    marital_status: '',
    past_due_bills: '',
    monthly_income: '',
    monthly_bills: ''
  })

  // Financial form error state
  const [financialErrors, setFinancialErrors] = useState({})
  
  // Financial form loading state
  const [isFinancialSubmitting, setIsFinancialSubmitting] = useState(false)
  const [financialSubmitStatus, setFinancialSubmitStatus] = useState(null) // 'success', 'error', null

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Get current path for active link detection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPath(window.location.pathname)
    }
  }, [])

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown')) {
        setIsLanguageDropdownOpen(false)
      }
    }

    if (isLanguageDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isLanguageDropdownOpen])

  // Check if current path is active
  const isActiveLink = useCallback((path) => {
    // Use passed location prop first, then fallback to currentPath state
    const pathname = location?.pathname || currentPath
    if (path === '/' && pathname === '/') return true
    if (path !== '/' && pathname.startsWith(path)) return true
    return false
  }, [location, currentPath])

  // Get language-aware path for navigation
  const getLanguagePath = useCallback((path) => {
    if (language === 'es') {
      return path === '/' ? '/es' : `/es${path}`
    }
    return path
  }, [language])

  const getSwitchLangPath = useCallback((path, toLang) => {
    if (toLang === 'es') {
      return path === '/' ? '/es' : `/es${path}`
    }
    // toLang is 'en'
    if (path.startsWith('/es/')) {
      return path.substring(3) // Remove '/es'
    } else if (path === '/es') {
      return '/'
    }
    return path
  }, [])

  // Handle language switching
  const handleLanguageSwitch = useCallback((newLanguage) => {
    setIsLanguageDropdownOpen(false)
    
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      let pathWithoutLang = currentPath
      
      // Remove existing language prefix
      if (currentPath.startsWith('/es/')) {
        pathWithoutLang = currentPath.substring(3) // Remove '/es'
      } else if (currentPath === '/es') {
        pathWithoutLang = '/'
      }
      // For paths that don't start with /es, they're already English paths
      
      let newPath
      if (newLanguage === 'en') {
        // English: use path without language prefix
        newPath = pathWithoutLang
      } else if (newLanguage === 'es') {
        // Spanish: add /es prefix
        if (pathWithoutLang === '/') {
          newPath = '/es'
        } else {
          newPath = `/es${pathWithoutLang}`
        }
      }
      
      console.log('Language switch - Current:', currentPath, 'New:', newPath)
      window.location.href = newPath
    }
  }, [])

  // Validation functions for financial form
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }, [])

  const validateUSPhoneNumber = useCallback((phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '')
    // US phone numbers should be exactly 10 digits
    return cleanPhone.length === 10
  }, [])

  const formatPhoneNumber = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '')
    if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    }
    return phone
  }, [])

  const formatPhoneNumberAsTyping = useCallback((phone) => {
    const cleanPhone = phone.replace(/\D/g, '').slice(0, 10) // Limit to 10 digits
    
    if (cleanPhone.length === 0) return ''
    if (cleanPhone.length <= 3) return cleanPhone
    if (cleanPhone.length <= 6) return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3)}`
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`
  }, [])

  // Financial form validation
  const validateFinancialForm = useCallback(() => {
    const errors = {}

    if (!financialData.name.trim()) {
      errors.name = t('financialModal.validation.nameRequired')
    }

    if (!financialData.address.trim()) {
      errors.address = t('financialModal.validation.addressRequired')
    }

    if (!financialData.email.trim()) {
      errors.email = t('financialModal.validation.emailRequired')
    } else if (!validateEmail(financialData.email)) {
      errors.email = t('financialModal.validation.emailInvalid')
    }

    if (!financialData.phone.trim()) {
      errors.phone = t('financialModal.validation.phoneRequired')
    } else if (!validateUSPhoneNumber(financialData.phone)) {
      errors.phone = t('financialModal.validation.phoneInvalid')
    }

    if (!financialData.dob) {
      errors.dob = t('financialModal.validation.dobRequired')
    } else {
      const birthDate = new Date(financialData.dob)
      const now = new Date()
      const age = now.getFullYear() - birthDate.getFullYear()
      if (age < 18) {
        errors.dob = t('financialModal.validation.dobMinAge')
      }
    }

    if (!financialData.due_date) {
      errors.due_date = t('financialModal.validation.dueDateRequired')
    }

    if (!financialData.baby_gender) {
      errors.baby_gender = t('financialModal.validation.genderRequired')
    }

    if (!financialData.marital_status) {
      errors.marital_status = t('financialModal.validation.maritalStatusRequired')
    }

    return errors
  }, [financialData, validateEmail, validateUSPhoneNumber, t])

  // API submission function
  const submitToAPI = useCallback(async (payload) => {
    const response = await fetch(`${process.env.PF_API_URL}DomainForm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        account: accountId,
        agency: agencyId,
        family: familyName,
        domain: typeof window !== 'undefined' ? window.location.href : ''
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }

    return await response.json()
  }, [accountId, agencyId, familyName])

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (isScheduleModalOpen || isFinancialModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isScheduleModalOpen, isFinancialModalOpen])

  // Close mobile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-dropdown') && !event.target.closest('.language-dropdown')) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isMobileMenuOpen])

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }, [isMobileMenuOpen])

  const toggleLanguageDropdown = useCallback((e) => {
    e.stopPropagation() // Prevent mobile menu from closing
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
  }, [isLanguageDropdownOpen])

  const openScheduleModal = useCallback(() => {
    setIsScheduleModalOpen(true)
  }, [])

  const closeScheduleModal = useCallback(() => {
    setIsScheduleModalOpen(false)
  }, [])

  const openFinancialModal = useCallback(() => {
    setIsFinancialModalOpen(true)
    setFinancialErrors({})
    setFinancialSubmitStatus(null)
  }, [])

  const closeFinancialModal = useCallback(() => {
    setIsFinancialModalOpen(false)
    setFinancialErrors({})
    setFinancialSubmitStatus(null)
    setFinancialData({
      name: '',
      address: '',
      email: '',
      phone: '',
      dob: '',
      due_date: '',
      baby_gender: '',
      marital_status: '',
      past_due_bills: '',
      monthly_income: '',
      monthly_bills: ''
    })
  }, [])

  const handleFinancialInputChange = useCallback((e) => {
    const { name, value } = e.target
    
    let formattedValue = value
    
    // Format phone number as user types
    if (name === 'phone') {
      formattedValue = formatPhoneNumberAsTyping(value)
    }
    
    setFinancialData(prev => ({
      ...prev,
      [name]: formattedValue
    }))
    
    // Clear error when user starts typing
    if (financialErrors[name]) {
      setFinancialErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Clear submit status when user makes changes
    if (financialSubmitStatus) {
      setFinancialSubmitStatus(null)
    }
  }, [financialErrors, formatPhoneNumberAsTyping, financialSubmitStatus])

  // Scroll to top of modal
  const scrollModalToTop = useCallback(() => {
    const modal = document.querySelector('.financial-modal-content')
    if (modal) {
      modal.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  const handleFinancialSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    const errors = validateFinancialForm()
    if (Object.keys(errors).length > 0) {
      setFinancialErrors(errors)
      setFinancialSubmitStatus('error')
      // Scroll to top of modal to show errors
      setTimeout(scrollModalToTop, 100)
      return
    }

    setIsFinancialSubmitting(true)
    setFinancialSubmitStatus(null)
    
    try {
      let recaptchaToken = null
      
      // Get reCAPTCHA token
      if (recaptchaSiteKey) {
        recaptchaToken = await executeRecaptcha('financial_form_submit')
      }

      const payload = {
        type: 'FinancialWorksheet',
        name: familyName,
        ...financialData,
        phone: formatPhoneNumber(financialData.phone),
        ...(recaptchaToken && { recaptcha_token: recaptchaToken })
      }

      await submitToAPI(payload)
      
      setFinancialSubmitStatus('success')
      
      // Scroll to top of modal to show success message
      setTimeout(scrollModalToTop, 100)
      
      // Close modal after successful submission with delay to show success message
      setTimeout(() => {
        closeFinancialModal()
      }, 5000)
      
    } catch (error) {
      console.error('Error submitting financial form:', error)
      setFinancialSubmitStatus('error')
      
      // Check if error is reCAPTCHA related
      if (error.message.includes('reCAPTCHA')) {
        setFinancialErrors({ 
          submit: t('financialModal.securityError')
        })
      } else {
        setFinancialErrors({ 
          submit: t('financialModal.errorMessage')
        })
      }
      
      // Scroll to top of modal to show error message
      setTimeout(scrollModalToTop, 100)
    } finally {
      setIsFinancialSubmitting(false)
    }
  }, [validateFinancialForm, executeRecaptcha, recaptchaLoaded, recaptchaSiteKey, financialData, formatPhoneNumber, submitToAPI, familyName, closeFinancialModal, scrollModalToTop, t])

  // Financial form status messages
  const financialStatusMessage = useMemo(() => {
    if (financialSubmitStatus === 'success') {
      return (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" role="alert">
          <div className="flex items-center">
            <span className="mr-2">✅</span>
            <span className="font-semibold font-heading-sub">{t('financialModal.successMessage')}</span>
          </div>
        </div>
      )
    }
    
    if (financialSubmitStatus === 'error' && financialErrors.submit) {
      return (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold font-heading-sub">{t('common.error')}</span>
          </div>
          <p className="mt-1 text-sm font-body">{financialErrors.submit}</p>
        </div>
      )
    }
    
    return null
  }, [financialSubmitStatus, financialErrors.submit, t])

  // Show warning if reCAPTCHA is not configured
  const recaptchaWarning = useMemo(() => {
    if (!recaptchaSiteKey) {
      return (
        <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg" role="alert">
          <div className="flex items-center">
            <span className="mr-2">⚠️</span>
            <span className="font-semibold font-heading-sub">Warning</span>
          </div>
          <p className="mt-1 text-sm font-body">{t('financialModal.recaptchaWarning')}</p>
        </div>
      )
    }
    return null
  }, [recaptchaSiteKey, t])

  // Error message component
  const ErrorMessage = useCallback(({ error }) => {
    if (!error) return null
    return <p className="text-red-500 text-sm mt-1 font-body">{error}</p>
  }, [])

  // Language switcher component - Desktop version
  const LanguageSwitcher = () => (
    <div className="relative language-dropdown">
      <button
        onClick={toggleLanguageDropdown}
        className={`flex items-center space-x-2 text-gray-700 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
          isScrolled ? 'text-xs' : 'text-sm'
        }`}
        aria-label={t('language.current')}
        aria-expanded={isLanguageDropdownOpen}
      >
        <span className="material-icons" style={{ fontSize: isScrolled ? '16px' : '18px' }}>
          language
        </span>
        <span>{language.toUpperCase()}</span>
        <span className="material-icons" style={{ fontSize: isScrolled ? '14px' : '16px' }}>
          {isLanguageDropdownOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isLanguageDropdownOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border z-50 min-w-32">
          <div className="py-2">
            {languages.map((lng) => (
              <Link
                key={lng}
                to={getSwitchLangPath(originalPath || '/', lng)}
                language={lng}
                className={`block px-4 py-2 text-sm font-medium transition-colors no-underline cursor-pointer font-heading-sub ${
                  lng === language 
                    ? 'text-dark bg-green-50' 
                    : 'text-gray-700 hover:bg-gray-50 hover:text-accent'
                }`}
                onClick={() => setIsLanguageDropdownOpen(false)}
              >
                {lng === 'en' ? 'English' : 'Español'}
                {lng === language && (
                  <span className="ml-2 text-dark">✓</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // Mobile Language Switcher Component - Fixed to prevent menu closing on first click
  const MobileLanguageSwitcher = () => (
    <div className="relative language-dropdown">
      <button
        onClick={toggleLanguageDropdown}
        className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer px-4 py-3 w-full text-left font-body"
        aria-label={t('language.current')}
        aria-expanded={isLanguageDropdownOpen}
      >
        <span className="material-icons" style={{ fontSize: '18px' }}>
          language
        </span>
        <span className="text-sm font-medium font-body">
          Language: <span className="uppercase">{language === 'en' ? 'English' : 'Español'}</span>
        </span>
        <span className="ml-auto material-icons" style={{ fontSize: '18px' }}>
          {isLanguageDropdownOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isLanguageDropdownOpen && (
        <div className="bg-gray-50 border-t">
          {languages.map((lng) => (
            <div key={lng}>
              {originalPath ? (
                <Link
                  to={getSwitchLangPath(originalPath || '/', lng)}
                  language={lng}
                  className={`block px-8 py-3 text-sm font-medium transition-colors no-underline cursor-pointer font-body ${
                    lng === language 
                      ? 'text-dark bg-green-50' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-accent'
                  }`}
                  onClick={() => {
                    setIsLanguageDropdownOpen(false)
                    setIsMobileMenuOpen(false)
                  }}
                >
                  {lng === 'en' ? 'English' : 'Español'}
                  {lng === language && (
                    <span className="ml-2 text-dark">✓</span>
                  )}
                </Link>
              ) : (
                <button
                  onClick={() => {
                    handleLanguageSwitch(lng)
                    setIsMobileMenuOpen(false)
                  }}
                  className={`w-full text-left block px-8 py-3 text-sm font-medium transition-colors no-underline cursor-pointer font-body ${
                    lng === language 
                      ? 'text-accent bg-green-50' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-accent'
                  }`}
                >
                  {lng === 'en' ? 'English' : 'Español'}
                  {lng === language && (
                    <span className="ml-2 text-accent">✓</span>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      {/* Sticky Header Container */}
      <div className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'translate-y-0' : '-translate-y-full md:translate-y-0'
      }`}>
        {/* Top Action Bar */}
        <div className={`bg-accent text-gray-700 transition-all duration-300 ${
          isScrolled ? 'py-3' : 'py-4'
        }`}>
          <div className="max-w-7xl mx-auto px-4">
            {/* Desktop Action Bar */}
            <div className="hidden md:flex justify-center items-center space-x-12">
              <button 
                onClick={() => window.location.href = `tel:${callNumber}`}
                className={`flex items-center space-x-2 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
                  isScrolled ? 'text-xs' : 'text-sm'
                }`}
                aria-label={t('header.callNow')}
              >
                <span className="material-icons" style={{ fontSize: isScrolled ? '18px' : '20px' }}>
                  call
                </span>
                <span>{t('header.callNow')}</span>
              </button>
              
              <button 
                onClick={() => window.location.href = `sms:${textNumber}?body=I would like quick contact on ${familyName}`}
                className={`flex items-center space-x-2 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
                  isScrolled ? 'text-xs' : 'text-sm'
                }`}
                aria-label={t('header.textUs')}
              >
                <span className="material-icons" style={{ fontSize: isScrolled ? '18px' : '20px' }}>
                  textsms
                </span>
                <span>{t('header.textUs')}</span>
              </button>
              
              <button 
                onClick={() => window.location.href = `mailto:${bmContactEmail}`}
                className={`flex items-center space-x-2 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
                  isScrolled ? 'text-xs' : 'text-sm'
                }`}
                aria-label={t('header.email')}
              >
                <span className="material-icons" style={{ fontSize: isScrolled ? '18px' : '20px' }}>
                  mail
                </span>
                <span>{t('header.email')}</span>
              </button>
              
              <button 
                onClick={openScheduleModal}
                className={`flex items-center space-x-2 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
                  isScrolled ? 'text-xs' : 'text-sm'
                }`}
                aria-label={t('header.schedule')}
              >
                <span className="material-icons" style={{ fontSize: isScrolled ? '18px' : '20px' }}>
                  event
                </span>
                <span>{t('header.schedule')}</span>
              </button>
              
              <button 
                onClick={openFinancialModal}
                className={`flex items-center space-x-2 hover:text-gray-600 transition-colors font-heading-sub font-semibold uppercase tracking-wider cursor-pointer ${
                  isScrolled ? 'text-xs' : 'text-sm'
                }`}
                aria-label={t('header.financial')}
              >
                <span className="material-icons" style={{ fontSize: isScrolled ? '18px' : '20px', margin: 0 }}>
                  attach_money
                </span>
                <span>{isScrolled ? t('header.financialShort') : t('header.financial')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Navigation Header - Desktop Only */}
        <header className="bg-white shadow-sm hidden md:block">
          <div className="max-w-7xl mx-auto px-4">
            <div className={`flex justify-center items-center transition-all duration-300 ${
              isScrolled ? 'py-3' : 'py-6'
            }`}>
              {/* Desktop Navigation */}
              <nav className={`flex transition-all duration-300 ${
                isScrolled ? 'space-x-8' : 'space-x-16'
              }`}>
                <Link 
                  to={getLanguagePath('/')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.home')}
                </Link>
                <Link 
                  to={getLanguagePath('/about')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/about') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.aboutUs')}
                </Link>
                <Link 
                  to={getLanguagePath('/letters')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/letters') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.letters')}
                </Link>
                <Link 
                  to={getLanguagePath('/gallery')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/gallery') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.glimpses')}
                </Link>
                <Link 
                  to={getLanguagePath('/faq')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/faq') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.faq')}
                </Link>
                <Link 
                  to={getLanguagePath('/contact')} 
                  className={`font-semibold transition-colors no-underline cursor-pointer uppercase tracking-wide font-heading-sub ${
                    isScrolled ? 'text-xs' : 'text-sm'
                  } ${
                    isActiveLink('/contact') 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('navigation.contact')}
                </Link>

                {/* Language Switcher - As Nav Child */}
                <LanguageSwitcher />
              </nav>
            </div>
          </div>
        </header>
      </div>

      {/* Mobile Sticky Green Bar - Always visible on mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40">
        <div className="bg-accent text-gray-700 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center">
              {/* Contact Options */}
              <button 
                onClick={() => window.location.href = `tel:${callNumber}`}
                className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer flex-1"
                aria-label={t('header.callNow')}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>call</span>
              </button>
              
              <button 
                onClick={() => window.location.href = `sms:${textNumber}?body=I would like quick contact on ${familyName}`}
                className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer flex-1"
                aria-label={t('header.textUs')}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>textsms</span>
              </button>
              
              <button 
                onClick={() => window.location.href = `mailto:${bmContactEmail}`}
                className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer flex-1"
                aria-label={t('header.email')}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>mail</span>
              </button>
              
              <button 
                onClick={openScheduleModal}
                className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer flex-1"
                aria-label={t('header.schedule')}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>event</span>
              </button>
              
              <button 
                onClick={openFinancialModal}
                className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer flex-1"
                aria-label={t('header.financial')}
              >
                <span className="material-icons" style={{ fontSize: '20px', margin: 0 }}>attach_money</span>
              </button>

              {/* Menu Button */}
              <div className="flex flex-col items-center flex-1 relative mobile-dropdown">
                <button 
                  onClick={toggleMobileMenu}
                  className="flex flex-col items-center hover:text-gray-600 transition-colors cursor-pointer"
                  aria-label={isMobileMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
                  aria-expanded={isMobileMenuOpen}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isMobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                  <span className="text-xs font-medium uppercase mt-0.5 font-heading-sub">Menu</span>
                </button>

                {/* Mobile Navigation Dropdown Menu */}
                {isMobileMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border z-50">
                    <nav className="py-2">
                      <Link 
                        to={getLanguagePath('/')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.home').toUpperCase()}
                      </Link>
                      <Link 
                        to={getLanguagePath('/about')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/about') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.aboutUs').toUpperCase()}
                      </Link>
                      <Link 
                        to={getLanguagePath('/letters')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/letters') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.letters').toUpperCase()}
                      </Link>
                      <Link 
                        to={getLanguagePath('/gallery')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/gallery') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.glimpses').toUpperCase()}
                      </Link>
                      <Link 
                        to={getLanguagePath('/faq')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/faq') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.faq').toUpperCase()}
                      </Link>
                      <Link 
                        to={getLanguagePath('/contact')} 
                        className={`block px-4 py-3 text-sm font-medium transition-all duration-200 cursor-pointer font-heading-sub border-l-4 ${
                          isActiveLink('/contact') 
                            ? 'text-primary bg-green-50 border-primary' 
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary border-transparent hover:border-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('navigation.contact').toUpperCase()}
                      </Link>
                      
                      {/* Language Switcher in Mobile Menu */}
                      <div className="border-t mt-2 pt-2">
                        <MobileLanguageSwitcher />
                      </div>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Spacer - Prevents content from hiding behind fixed header */}
      <div className="md:hidden h-16"></div> {/* Mobile spacer */}
      <div className="hidden md:block" style={{height: '120px'}}></div> {/* Desktop spacer */}

      {/* Schedule Form Modal */}
      <ScheduleForm
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        accountId={accountId}
        agencyId={agencyId}
        familyName={familyName}
      />

      {/* Financial Support Worksheet Modal */}
      {isFinancialModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black bg-opacity-70 transition-opacity cursor-pointer"
            onClick={closeFinancialModal}
          ></div>
          
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto financial-modal-content">
            <div className="flex items-center justify-center p-6 border-b border-gray-200 relative">
              <h2 className="text-2xl font-semibold text-center font-heading-main">{t('financialModal.title')}</h2>
              <button
                onClick={closeFinancialModal}
                className="absolute right-6 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                disabled={isFinancialSubmitting}
                aria-label={t('common.close')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 pt-6">
              <p className="text-sm text-gray-600 text-center mb-6 font-body">
                {t('financialModal.disclaimer')}
              </p>
            </div>

            <form onSubmit={handleFinancialSubmit} className="p-6 space-y-6">
              {/* reCAPTCHA Warning */}
              {recaptchaWarning}

              {/* Status Messages */}
              {financialStatusMessage}

              <div>
                <label htmlFor="financial-name" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.name')} *
                </label>
                <input
                  type="text"
                  id="financial-name"
                  name="name"
                  value={financialData.name}
                  onChange={handleFinancialInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-text font-body ${
                    financialErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                />
                <ErrorMessage error={financialErrors.name} />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.address')} *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={financialData.address}
                  onChange={handleFinancialInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-text font-body ${
                    financialErrors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                />
                <ErrorMessage error={financialErrors.address} />
              </div>

              <div>
                <label htmlFor="financial-email" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.email')} *
                </label>
                <input
                  type="email"
                  id="financial-email"
                  name="email"
                  value={financialData.email}
                  onChange={handleFinancialInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-text font-body ${
                    financialErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                />
                <ErrorMessage error={financialErrors.email} />
              </div>

              <div>
                <label htmlFor="financial-phone" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.phone')} *
                </label>
                <input
                  type="tel"
                  id="financial-phone"
                  name="phone"
                  value={financialData.phone}
                  onChange={handleFinancialInputChange}
                  placeholder={t('financialModal.placeholders.phone')}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-text font-body ${
                    financialErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                />
                <ErrorMessage error={financialErrors.phone} />
              </div>

              <div>
                <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.dob')} *
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    value={financialData.dob}
                    onChange={handleFinancialInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-pointer font-body ${
                      financialErrors.dob ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isFinancialSubmitting}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <ErrorMessage error={financialErrors.dob} />
              </div>

              <div>
                <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.dueDate')} *
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="due_date"
                    name="due_date"
                    value={financialData.due_date}
                    onChange={handleFinancialInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors cursor-pointer font-body ${
                      financialErrors.due_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isFinancialSubmitting}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <ErrorMessage error={financialErrors.due_date} />
              </div>

              <div>
                <label id="baby_gender-label" htmlFor="baby_gender" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.babyGender')} *
                </label>
                <select
                  id="baby_gender"
                  name="baby_gender"
                  value={financialData.baby_gender}
                  onChange={handleFinancialInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors bg-white cursor-pointer font-body ${
                    financialErrors.baby_gender ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                  aria-labelledby="baby_gender-label"
                  aria-describedby={financialErrors.baby_gender ? "baby_gender-error" : undefined}
                  aria-required="true"
                >
                  <option value="">{t('financialModal.options.gender.placeholder')}</option>
                  <option value="Male">{t('financialModal.options.gender.male')}</option>
                  <option value="Female">{t('financialModal.options.gender.female')}</option>
                  <option value="Unknown">{t('financialModal.options.gender.unknown')}</option>
                </select>
                <ErrorMessage error={financialErrors.baby_gender} />
              </div>

              <div>
                <label id="marital_status-label" htmlFor="marital_status" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.maritalStatus')} *
                </label>
                <select
                  id="marital_status"
                  name="marital_status"
                  value={financialData.marital_status}
                  onChange={handleFinancialInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors bg-white cursor-pointer font-body ${
                    financialErrors.marital_status ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isFinancialSubmitting}
                  aria-labelledby="marital_status-label"
                  aria-describedby={financialErrors.marital_status ? "marital_status-error" : undefined}
                  aria-required="true"
                >
                  <option value="">{t('financialModal.options.maritalStatus.placeholder')}</option>
                  <option value="Single">{t('financialModal.options.maritalStatus.single')}</option>
                  <option value="Married">{t('financialModal.options.maritalStatus.married')}</option>
                  <option value="Divorced">{t('financialModal.options.maritalStatus.divorced')}</option>
                  <option value="Separated">{t('financialModal.options.maritalStatus.separated')}</option>
                  <option value="Widowed">{t('financialModal.options.maritalStatus.widowed')}</option>
                </select>
                <ErrorMessage error={financialErrors.marital_status} />
              </div>

              <div>
                <label htmlFor="past_due_bills" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.pastDueBills')}
                </label>
                <textarea
                  id="past_due_bills"
                  name="past_due_bills"
                  value={financialData.past_due_bills}
                  onChange={handleFinancialInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors resize-none font-body text-sm cursor-text"
                  placeholder={t('financialModal.placeholders.pastDueBills')}
                  disabled={isFinancialSubmitting}
                />
              </div>

              <div>
                <label htmlFor="monthly_income" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.monthlyIncome')}
                </label>
                <textarea
                  id="monthly_income"
                  name="monthly_income"
                  value={financialData.monthly_income}
                  onChange={handleFinancialInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors resize-none font-body text-sm cursor-text"
                  placeholder={t('financialModal.placeholders.monthlyIncome')}
                  disabled={isFinancialSubmitting}
                />
              </div>

              <div>
                <label htmlFor="monthly_bills" className="block text-sm font-medium text-gray-700 mb-2 font-heading-sub">
                  {t('financialModal.fields.monthlyBills')}
                </label>
                <textarea
                  id="monthly_bills"
                  name="monthly_bills"
                  value={financialData.monthly_bills}
                  onChange={handleFinancialInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-colors resize-none font-body text-sm cursor-text"
                  placeholder={t('financialModal.placeholders.monthlyBills')}
                  disabled={isFinancialSubmitting}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeFinancialModal}
                  className="btn-primary-dark flex-1 px-6 py-3 font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 cursor-pointer font-heading-sub"
                  disabled={isFinancialSubmitting}
                >
                  {t('financialModal.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 px-6 py-3 font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center justify-center cursor-pointer font-heading-sub"
                  disabled={isFinancialSubmitting}
                >
                  {isFinancialSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('financialModal.buttons.submitting')}
                    </>
                  ) : (
                    t('financialModal.buttons.submit')
                  )}
                </button>
              </div>

              {/* reCAPTCHA Badge Note */}
              {recaptchaSiteKey && (
                <div className="text-center pt-4">
                  <p className="text-xs text-gray-500 font-body">
                    {t('financialModal.recaptchaNotice')}{' '}
                    <a 
                      href="https://policies.google.com/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a 
                      href="https://policies.google.com/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default AdoptionHeader